import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {NavigateFunction} from 'react-router-dom';
import {EstimateWithComponentsDto} from '../../../../shared/dtos/estimate-with-components.dto';
import {EstimateTreeDto} from '../../../../shared/dtos/estimate-tree.dto';
import {EstimateGateDetailsDto} from '../../../../shared/dtos/estimate-gate.dto';
import {EstimateAdditionalComponentDto} from '../../../../shared/dtos/estimate-additional-component.dto';
import {EstimateDragPathDto, LatLngDto} from '../../../../shared/dtos/estimate-drag-path.dto';
import {EstimateCraneConnectionDto} from '../../../../shared/dtos/estimate-crane-connection.dto';
import {nextOpenLocation} from '../../../../shared/misc/location-helpers';
import {PhotoEdit, saveImageEdit, uploadImage} from '../../../../shared/services/image-service';
import {createGate, deleteGate as deleteGateApi, hideGateImage, updateGate} from '../../estimate-gate.service';
import {createAdditionalComponent, deleteAdditionalComponent, updateAdditionalComponent} from '../../estimate-additional-component.service';
import {createDragPath, deleteDragPath, updateDragPath} from '../../estimate-drag-path.service';
import {createCraneConnection, deleteCraneConnection, updateCraneConnection} from '../../estimate-crane-connection.service';
import {
  CHIPPER_LENGTH_METERS, CRANE_ART_PER_TRUCK, CRANE_LENGTH_METERS, GATE_ICON_WIDTH_DP,
  chipperCenterFromRear, chipperRearCenter, craneRear, craneTurretFromRear, defaultPileSpot, distanceMeters,
  dragPathUpdate, metersPerPx, pathDistanceFeet, pathTouchesGate,
} from './map-geometry';
import {GATE_MAX_INCHES} from './gate-width';

/*
 * The job site map's state and every save (SQ-621): the port of Android's
 * MapWorkspaceController.kt. Gates, trucks & chippers, cranes, drag routes and
 * crane connections live on the record's estimate (so they survive the map
 * closing, or swapping between beside the record and full size); work items
 * stay the record's (useWorkItems), and the map moves them through it.
 *
 * Every change is shown at once and saved at once; a save that fails puts the
 * change back and says so, in Android's words.
 */

export const CHIPPER_TYPE = 'chipper';
export const CRANE_TYPE = 'crane';

/** The zoom the map opens at, Android's SITE_ZOOM: which routes cross a gate is judged at the zoom on screen. */
export const SITE_ZOOM = 19;

interface MapData {
  gates: EstimateGateDetailsDto[];
  additionalComponents: EstimateAdditionalComponentDto[];
  dragPaths: EstimateDragPathDto[];
  craneConnections: EstimateCraneConnectionDto[];
}
const FIELDS: (keyof MapData)[] = ['gates', 'additionalComponents', 'dragPaths', 'craneConnections'];
const EMPTY: MapData = {gates: [], additionalComponents: [], dragPaths: [], craneConnections: []};

// One empty list, so a record without a field does not look changed on every render.
const NONE: never[] = [];
const pick = (e: EstimateWithComponentsDto): MapData => ({
  gates: e.gates ?? NONE,
  additionalComponents: e.additionalComponents ?? NONE,
  dragPaths: e.dragPaths ?? NONE,
  craneConnections: e.craneConnections ?? NONE,
});

/** Where the map was looking, and what it has already said: kept while the map is closed or changes place. */
export interface SiteMapView {
  center?: LatLngDto;
  zoom?: number;
  /** The person moved the map (or it was framed once): it is never re-framed. */
  framed: boolean;
  hintDismissed: boolean;
}

/** The 1-based numbers on the map, by ascending id, so a thing keeps its number across reloads. */
export const localIds = (ids: number[]): Map<number, number> =>
  new Map([...ids].sort((a, b) => a - b).map((id, i) => [id, i + 1]));

const norm360 = (d: number) => ((d % 360) + 360) % 360;

const errorText = (e: unknown, fallback: string) => e instanceof Error && e.message ? e.message : fallback;

/** One machine's saves in order; `confirmed` is what the server last took, kept while a burst of saves is on its way. */
interface MachineQueue {
  tail: Promise<unknown>;
  issued: number;
  confirmed?: {component: EstimateAdditionalComponentDto; paths: EstimateDragPathDto[]};
}

export interface SiteMapOptions {
  navigate: NavigateFunction;
  estimateId: number;
  estimate: EstimateWithComponentsDto | undefined;
  setEstimate: React.Dispatch<React.SetStateAction<EstimateWithComponentsDto | undefined>>;
  trees: EstimateTreeDto[];
  /** Moves the work item through the record; resolves true once saved. */
  moveWorkItem: (treeId: number, lat: number, lng: number) => Promise<boolean>;
  setDragDistanceFeet: (treeId: number, feet: number | undefined) => void;
  editable: boolean;
  onError: (message: string) => void;
}

export function useSiteMap(o: SiteMapOptions) {
  const {navigate, estimateId} = o;
  const opts = useRef(o);
  opts.current = o;
  const fail = (fallback: string) => (e: unknown) => opts.current.onError(errorText(e, fallback));

  // The map's own copy, adopted from the estimate whenever something else changed it (the record's add menu adds gates).
  const data = useRef<MapData>(EMPTY);
  const pushed = useRef<MapData | undefined>(undefined);
  if (o.estimate) {
    const incoming = pick(o.estimate);
    const before = pushed.current;
    const changed = FIELDS.filter(f => !before || incoming[f] !== before[f]);
    if (changed.length) {
      changed.forEach(f => { data.current = {...data.current, [f]: incoming[f]}; });
      pushed.current = incoming;
    }
  }

  const [working, setWorking] = useState(false);
  /** Gate photos still uploading, as local previews. */
  const [gateUploads, setGateUploads] = useState<Record<number, string[]>>({});
  const view = useRef<SiteMapView>({framed: false, hintDismissed: false});
  const viewZoom = useRef(SITE_ZOOM);

  const tree = (id: number) => opts.current.trees.find(t => t.id === id);

  // ---- Gate crossings (SQ-527) ---------------------------------------------------------------------

  const crossings = (d: MapData): Set<string> => {
    const pairs = new Set<string>();
    d.gates.forEach(g => d.dragPaths.forEach(p => {
      if (pathTouchesGate(p.points, {lat: g.lat, lng: g.lng}, viewZoom.current, g.heading ?? 0)) pairs.add(`${g.id}:${p.treeId}`);
    }));
    return pairs;
  };

  // One association write at a time, each sending the set as it stands when its turn comes: the endpoint replaces the whole set.
  const gateWrites = useRef<Promise<void>>(Promise.resolve());
  const persistGateWorkItems = (gateId: number) => {
    gateWrites.current = gateWrites.current.then(async () => {
      const gate = data.current.gates.find(g => g.id === gateId);
      if (!gate) return;
      try {
        await updateGate(navigate, estimateId, gateId, {associatedTreeIds: gate.associatedTrees.map(t => t.id)});
      } catch (e) {
        fail("Failed to save the gate's work items")(e);
      }
    });
  };

  const withTrees = (gate: EstimateGateDetailsDto, ids: number[]): EstimateGateDetailsDto => ({
    ...gate,
    associatedTrees: Array.from(new Set(ids))
      .map(id => tree(id) ?? gate.associatedTrees.find(t => t.id === id))
      .filter((t): t is EstimateTreeDto => !!t),
    // SQ-716: a work item that leaves the gate forgets the photos it hid, so reconnecting brings them all back (the server does the same).
    hiddenImages: gate.hiddenImages?.filter(h => ids.includes(h.treeId)),
  });

  /**
   * Applies a change and saves nothing: the callers save. A route through a
   * gate connects its work item to it, every time; only routes that LEFT a
   * gate disconnect, so a work item connected by hand stays connected.
   */
  const commit = (change: (d: MapData) => MapData) => {
    const before = data.current;
    let next = change(before);
    const was = crossings(before);
    const now = crossings(next);
    const touched: number[] = [];
    next = {
      ...next,
      gates: next.gates.map(gate => {
        const current = gate.associatedTrees.map(t => t.id);
        const ids = current.filter(id => !(was.has(`${gate.id}:${id}`) && !now.has(`${gate.id}:${id}`)));
        now.forEach(pair => {
          const [g, t] = pair.split(':').map(Number);
          if (g === gate.id && !ids.includes(t)) ids.push(t);
        });
        if (ids.length === current.length && ids.every((id, i) => id === current[i])) return gate;
        touched.push(gate.id);
        return withTrees(gate, ids);
      }),
    };
    if (FIELDS.every(f => next[f] === before[f])) return;
    data.current = next;
    pushed.current = next;
    opts.current.setEstimate(e => {
      if (!e) return e;
      const out = {...e};
      FIELDS.forEach(f => { if (next[f] !== before[f]) (out as Record<string, unknown>)[f] = next[f]; });
      return out;
    });
    touched.forEach(persistGateWorkItems);
  };

  const chippers = () => data.current.additionalComponents.filter(c => c.type === CHIPPER_TYPE);
  const cranes = () => data.current.additionalComponents.filter(c => c.type === CRANE_TYPE);

  const occupied = () => [
    ...opts.current.trees.map(t => ({lat: t.lat, lng: t.lng})),
    ...data.current.gates.map(g => ({lat: g.lat, lng: g.lng})),
    ...data.current.additionalComponents.map(c => ({lat: c.lat, lng: c.lng})),
  ];
  const spawn = (stepMeters?: number) => {
    const e = opts.current.estimate!;
    return nextOpenLocation({lat: e.lat, lng: e.lng}, occupied(), stepMeters);
  };

  // ---- Saves per machine --------------------------------------------------------------------------------

  // A machine's saves, its routes' with them, run one at a time: two quick drags whose saves landed out of
  // order left the server on the older spot and drag distance while the screen showed the newer.
  const machineQueues = useRef(new Map<number, MachineQueue>());
  const queueFor = (machineId: number) => {
    let q = machineQueues.current.get(machineId);
    if (!q) machineQueues.current.set(machineId, q = {tail: Promise.resolve(), issued: 0});
    return q;
  };
  /** Runs the save once every save already queued for the machine is done. */
  const inTurn = <T>(machineId: number, save: () => Promise<T>): Promise<T> => {
    const q = queueFor(machineId);
    const run = q.tail.then(save);
    q.tail = run.catch(() => undefined);
    return run;
  };

  // ---- Drag routes -------------------------------------------------------------------------------------

  /** Where a work item's red route starts: its crane's staging pile when it is on one, else the work item. */
  const routeStartFor = (treeId: number, treeAt?: LatLngDto): LatLngDto | undefined => {
    const t = tree(treeId);
    const at = treeAt ?? (t ? {lat: t.lat, lng: t.lng} : undefined);
    if (!at) return undefined;
    const link = data.current.craneConnections.find(c => c.treeId === treeId);
    if (!link) return at;
    if (link.pileLat != null && link.pileLng != null) return {lat: link.pileLat, lng: link.pileLng};
    const crane = cranes().find(c => c.id === link.craneId);
    return crane ? defaultPileSpot({lat: crane.lat, lng: crane.lng}, at) : at;
  };

  const applyPath = (pathId: number, points: LatLngDto[], distanceFeet: number, chipperTruckId?: number) =>
    commit(d => ({
      ...d,
      dragPaths: d.dragPaths.map(p => p.id !== pathId ? p : {...p, points, distanceFeet, chipperTruckId: chipperTruckId ?? p.chipperTruckId}),
    }));

  /** Puts a route back the way the server still has it (a stale dragDistanceFeet would misprice the job). */
  const restorePath = (previous: EstimateDragPathDto, previousFeet: number | undefined) => {
    commit(d => ({...d, dragPaths: d.dragPaths.map(p => p.id === previous.id ? previous : p)}));
    opts.current.setDragDistanceFeet(previous.treeId, previousFeet);
  };

  /** A route save that went through is what a later failed save of its truck puts back. */
  const routeSaved = (path: EstimateDragPathDto) => {
    const confirmed = machineQueues.current.get(path.chipperTruckId)?.confirmed;
    if (confirmed) confirmed.paths = [...confirmed.paths.filter(p => p.id !== path.id), path];
  };

  /** Saves a route in its truck's turn; resolves false when the save failed (and the route was put back). */
  const saveRoute = async (previous: EstimateDragPathDto, previousFeet: number | undefined, update: {points: LatLngDto[]; distanceFeet: number; chipperTruckId?: number}) => {
    try {
      await inTurn(previous.chipperTruckId, () => updateDragPath(navigate, estimateId, previous.id, update));
      routeSaved({...previous, ...update, chipperTruckId: update.chipperTruckId ?? previous.chipperTruckId});
      return true;
    } catch (e) {
      restorePath(previous, previousFeet);
      fail('Failed to update drag path')(e);
      return false;
    }
  };

  const persistPath = async (pathId: number, points: LatLngDto[]) => {
    const previous = data.current.dragPaths.find(p => p.id === pathId);
    if (!previous) return true;
    const previousFeet = tree(previous.treeId)?.dragDistanceFeet;
    const distanceFeet = pathDistanceFeet(points);
    applyPath(pathId, points, distanceFeet);
    opts.current.setDragDistanceFeet(previous.treeId, distanceFeet);
    return saveRoute(previous, previousFeet, {points, distanceFeet});
  };

  /** Re-starts a work item's route where it should start now, keeping its bends; resolves false when that save failed. */
  const followRouteStart = async (treeId: number, treeAt?: LatLngDto) => {
    const path = data.current.dragPaths.find(p => p.treeId === treeId);
    const start = routeStartFor(treeId, treeAt);
    if (!path || !start || path.points.length === 0) return true;
    const first = path.points[0];
    if (first.lat === start.lat && first.lng === start.lng) return true;
    return persistPath(path.id, [start, ...path.points.slice(1)]);
  };

  /** Lays the route straight from its start to its truck: a crane taking or letting go of the work item moves its start. */
  const resetRouteStraight = async (treeId: number) => {
    const path = data.current.dragPaths.find(p => p.treeId === treeId);
    const start = routeStartFor(treeId);
    const end = path?.points[path.points.length - 1];
    if (!path || !start || !end) return;
    await persistPath(path.id, [start, end]);
  };

  /** The truck moved or turned: its routes re-end at its rear, every bend kept (the route hinges at its last one). */
  const routesToRear = (d: MapData, truckId: number, rear: LatLngDto): MapData => ({
    ...d,
    dragPaths: d.dragPaths.map(p => p.chipperTruckId !== truckId || p.points.length === 0 ? p : {
      ...p, points: [...p.points.slice(0, -1), rear], distanceFeet: pathDistanceFeet([...p.points.slice(0, -1), rear]),
    }),
  });

  const connectsInFlight = useRef(new Set<number>());

  /** A truck panel's checkbox was checked: a straight route from the work item (or its pile) to the back of the chipper. */
  const connectWorkItem = async (chipperId: number, treeId: number) => {
    const chipper = chippers().find(c => c.id === chipperId);
    const t = tree(treeId);
    if (!chipper || !t) return;
    if (data.current.dragPaths.some(p => p.treeId === treeId)) return;
    if (connectsInFlight.current.has(treeId)) return;
    connectsInFlight.current.add(treeId);
    try {
      const start = routeStartFor(treeId) ?? {lat: t.lat, lng: t.lng};
      const points = [start, chipperRearCenter({lat: chipper.lat, lng: chipper.lng}, chipper.heading ?? 0)];
      const distanceFeet = pathDistanceFeet(points);
      const created = await createDragPath(navigate, estimateId, {estimateId, treeId, chipperTruckId: chipperId, points, distanceFeet});
      commit(d => ({...d, dragPaths: [...d.dragPaths.filter(p => p.treeId !== treeId), created]}));
      opts.current.setDragDistanceFeet(treeId, created.distanceFeet);
    } catch (e) {
      fail('Failed to create drag path')(e);
    } finally {
      connectsInFlight.current.delete(treeId);
    }
  };

  /** Unchecked: the route goes. */
  const disconnectWorkItem = async (treeId: number) => {
    const path = data.current.dragPaths.find(p => p.treeId === treeId);
    if (!path) return;
    commit(d => ({...d, dragPaths: d.dragPaths.filter(p => p.id !== path.id)}));
    opts.current.setDragDistanceFeet(treeId, undefined);
    try {
      await deleteDragPath(navigate, estimateId, path.id);
    } catch (e) {
      commit(d => d.dragPaths.some(p => p.id === path.id) ? d : {...d, dragPaths: [...d.dragPaths, path]});
      opts.current.setDragDistanceFeet(treeId, path.distanceFeet);
      fail('Failed to remove drag path')(e);
    }
  };

  /** A route point was dropped: dropped within 5 m of another truck, the route moves to it. */
  const movePathPoint = async (pathId: number, points: LatLngDto[], changedIndex: number) => {
    const path = data.current.dragPaths.find(p => p.id === pathId);
    if (!path) return;
    const previousFeet = tree(path.treeId)?.dragDistanceFeet;
    const update = dragPathUpdate(points, changedIndex, path.chipperTruckId, chippers());
    applyPath(pathId, update.points, update.distanceFeet, update.chipperTruckId);
    opts.current.setDragDistanceFeet(path.treeId, update.distanceFeet);
    await saveRoute(path, previousFeet, update);
  };

  // ---- Work items -----------------------------------------------------------------------------------------

  /** A pin was dropped: the work item moves through the record, then its route's start follows. */
  const moveWorkItem = async (treeId: number, lat: number, lng: number) => {
    if (await opts.current.moveWorkItem(treeId, lat, lng)) await followRouteStart(treeId, {lat, lng});
  };

  // A work item deleted from the record takes its route (the server drops its crane connection itself).
  // Only one that was there and went: a list that is not loaded yet must never read as every work item deleted.
  const treeIds = o.trees.map(t => t.id).join(',');
  const knownTrees = useRef<Set<number> | undefined>(undefined);
  useEffect(() => {
    const ids = new Set(opts.current.trees.map(t => t.id));
    const known = knownTrees.current;
    knownTrees.current = ids;
    if (!known || !o.estimate) return;
    const gone = new Set(Array.from(known).filter(id => !ids.has(id)));
    if (gone.size === 0) return;
    if (data.current.craneConnections.some(c => gone.has(c.treeId))) {
      commit(d => ({...d, craneConnections: d.craneConnections.filter(c => !gone.has(c.treeId))}));
    }
    data.current.dragPaths.filter(p => gone.has(p.treeId)).forEach(path => {
      commit(d => ({...d, dragPaths: d.dragPaths.filter(p => p.id !== path.id)}));
      deleteDragPath(navigate, estimateId, path.id).catch(e => {
        commit(d => d.dragPaths.some(p => p.id === path.id) ? d : {...d, dragPaths: [...d.dragPaths, path]});
        fail('Failed to remove drag path')(e);
      });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [treeIds]);

  // ---- Trucks & chippers --------------------------------------------------------------------------------

  /**
   * Moves or turns a machine, its routes re-ending at its rear, and saves it all in the machine's turn.
   * A failed save puts back what the server last took, unless a later drag is already queued: that one
   * sends the newer state. Resolves false when this save failed.
   */
  const persistComponent = async (previous: EstimateAdditionalComponentDto, at: LatLngDto, heading: number, message: string) => {
    const id = previous.id;
    const q = queueFor(id);
    if (!q.confirmed) q.confirmed = {component: previous, paths: data.current.dragPaths.filter(p => p.chipperTruckId === id)};
    const turn = ++q.issued;
    const latest = () => turn === q.issued;
    commit(d => {
      const moved = {...d, additionalComponents: d.additionalComponents.map(c => c.id === id ? {...c, lat: at.lat, lng: at.lng, heading} : c)};
      return previous.type === CHIPPER_TYPE ? routesToRear(moved, id, chipperRearCenter(at, heading)) : moved;
    });
    const component = {...previous, lat: at.lat, lng: at.lng, heading};
    const paths = previous.type === CHIPPER_TYPE ? data.current.dragPaths.filter(p => p.chipperTruckId === id) : [];
    paths.forEach(p => opts.current.setDragDistanceFeet(p.treeId, p.distanceFeet));
    const saved = await inTurn(id, async () => {
      const confirmed = q.confirmed!;
      try {
        await updateAdditionalComponent(navigate, estimateId, id, {
          lat: at.lat, lng: at.lng, type: previous.type ?? CHIPPER_TYPE, heading, notes: previous.notes, imageIds: previous.images?.map(i => i.id) ?? [],
        });
      } catch (e) {
        if (latest()) {
          commit(d => ({
            ...d,
            additionalComponents: d.additionalComponents.map(c => c.id === id ? confirmed.component : c),
            dragPaths: d.dragPaths.map(p => p.chipperTruckId === id ? confirmed.paths.find(b => b.id === p.id) ?? p : p),
          }));
          confirmed.paths.forEach(p => opts.current.setDragDistanceFeet(p.treeId, p.distanceFeet));
        }
        fail(message)(e);
        return false;
      }
      confirmed.component = component;
      await Promise.all(paths.map(p => updateDragPath(navigate, estimateId, p.id, {points: p.points, distanceFeet: p.distanceFeet})
        .then(() => routeSaved(p), e => {
          const old = confirmed.paths.find(b => b.id === p.id);
          if (latest() && old) restorePath(old, old.distanceFeet);
          fail('Failed to update drag path')(e);
        })));
      return true;
    });
    // The burst is over: the next drag starts from what the server holds now.
    if (latest()) q.confirmed = undefined;
    return saved;
  };

  const addComponent = async (type: string, stepMeters: number, message: string, onCreated?: (c: EstimateAdditionalComponentDto) => void) => {
    if (!opts.current.estimate) return;
    const at = spawn(stepMeters);
    setWorking(true);
    try {
      const created = await createAdditionalComponent(navigate, estimateId, {lat: at.lat, lng: at.lng, type});
      commit(d => ({...d, additionalComponents: [...d.additionalComponents, created]}));
      onCreated?.(created);
    } catch (e) {
      fail(message)(e);
    } finally {
      setWorking(false);
    }
  };

  /** Trucks spawn further apart than pins: the footprint is a true 45 ft. */
  const addChipperTruck = (onCreated?: (c: EstimateAdditionalComponentDto) => void) =>
    addComponent(CHIPPER_TYPE, CHIPPER_LENGTH_METERS * 0.9, 'Failed to add chipper truck', onCreated);

  /** Dragged by its body: the rear of the chipper landed here; the stored centre follows along its heading. */
  const moveChipperTruck = (id: number, rear: LatLngDto) => {
    const previous = chippers().find(c => c.id === id);
    if (!previous) return;
    const heading = previous.heading ?? 0;
    void persistComponent(previous, chipperCenterFromRear(rear, heading), heading, 'Failed to move chipper truck');
  };

  /** Turned on its half moon: the back stays planted, only the centre swings round it. */
  const rotateChipperTruck = (id: number, headingDeg: number) => {
    const previous = chippers().find(c => c.id === id);
    if (!previous) return;
    const heading = norm360(headingDeg);
    const rear = chipperRearCenter({lat: previous.lat, lng: previous.lng}, previous.heading ?? 0);
    void persistComponent(previous, chipperCenterFromRear(rear, heading), heading, 'Failed to move chipper truck');
  };

  /** Deleting a truck lets go of every route it had (no confirm, as on Android). */
  const deleteChipperTruck = async (id: number) => {
    const attached = data.current.dragPaths.filter(p => p.chipperTruckId === id);
    setWorking(true);
    try {
      for (const path of attached) {
        await deleteDragPath(navigate, estimateId, path.id);
        // Dropped one at a time, so a failure partway leaves exactly what the server still holds.
        opts.current.setDragDistanceFeet(path.treeId, undefined);
        commit(d => ({...d, dragPaths: d.dragPaths.filter(p => p.id !== path.id)}));
      }
      await deleteAdditionalComponent(navigate, estimateId, id);
      commit(d => ({...d, additionalComponents: d.additionalComponents.filter(c => c.id !== id)}));
    } catch (e) {
      fail('Failed to delete chipper truck')(e);
    } finally {
      setWorking(false);
    }
  };

  // ---- Cranes (SQ-527) --------------------------------------------------------------------------------

  const addCrane = (onCreated?: (c: EstimateAdditionalComponentDto) => void) =>
    addComponent(CRANE_TYPE, CRANE_LENGTH_METERS * CRANE_ART_PER_TRUCK, 'Failed to add crane', onCreated);

  const afterCraneMoved = (craneId: number) => data.current.craneConnections
    .filter(c => c.craneId === craneId && c.pileLat == null)
    .forEach(c => void followRouteStart(c.treeId));

  /** A crane is stored at its turret, which is what a drag holds. */
  const moveCrane = async (id: number, turret: LatLngDto) => {
    const previous = cranes().find(c => c.id === id);
    if (previous && await persistComponent(previous, turret, previous.heading ?? 0, 'Failed to move crane')) afterCraneMoved(id);
  };

  /** A crane turns about the rear centre of its truck: the turret swings round it. */
  const rotateCrane = async (id: number, headingDeg: number) => {
    const previous = cranes().find(c => c.id === id);
    if (!previous) return;
    const heading = norm360(headingDeg);
    const rear = craneRear({lat: previous.lat, lng: previous.lng}, previous.heading ?? 0);
    if (await persistComponent(previous, craneTurretFromRear(rear, heading), heading, 'Failed to move crane')) afterCraneMoved(id);
  };

  /** Deleting a crane frees every work item it had; their routes snap back to them. */
  const deleteCrane = async (id: number) => {
    setWorking(true);
    try {
      await deleteAdditionalComponent(navigate, estimateId, id);
      const freed = data.current.craneConnections.filter(c => c.craneId === id).map(c => c.treeId);
      commit(d => ({
        ...d,
        additionalComponents: d.additionalComponents.filter(c => c.id !== id),
        craneConnections: d.craneConnections.filter(c => c.craneId !== id),
      }));
      freed.forEach(t => void resetRouteStraight(t));
    } catch (e) {
      fail('Failed to delete crane')(e);
    } finally {
      setWorking(false);
    }
  };

  const craneConnectsInFlight = useRef(new Set<number>());

  /** One crane per work item: one already on another crane is not offered. */
  const connectCrane = async (craneId: number, treeId: number) => {
    if (!cranes().some(c => c.id === craneId)) return;
    if (data.current.craneConnections.some(c => c.treeId === treeId)) return;
    if (craneConnectsInFlight.current.has(treeId)) return;
    craneConnectsInFlight.current.add(treeId);
    try {
      const created = await createCraneConnection(navigate, estimateId, {craneId, treeId});
      commit(d => ({...d, craneConnections: [...d.craneConnections.filter(c => c.treeId !== treeId), created]}));
      // The brush now goes to the pile: the red route starts there, laid straight (its bends were for a walk from the work item).
      await resetRouteStraight(treeId);
    } catch (e) {
      fail('Failed to connect the work item to the crane')(e);
    } finally {
      craneConnectsInFlight.current.delete(treeId);
    }
  };

  const disconnectCrane = async (treeId: number) => {
    const link = data.current.craneConnections.find(c => c.treeId === treeId);
    if (!link) return;
    commit(d => ({...d, craneConnections: d.craneConnections.filter(c => c.id !== link.id)}));
    try {
      await deleteCraneConnection(navigate, estimateId, link.id);
      await resetRouteStraight(treeId);
    } catch (e) {
      commit(d => d.craneConnections.some(c => c.treeId === treeId) ? d : {...d, craneConnections: [...d.craneConnections, link]});
      fail('Failed to disconnect the work item from the crane')(e);
    }
  };

  /**
   * A pile that was moved stays where it was put from then on. Its route follows only once the pile is
   * saved, and a route save that fails takes the pile back too, so the saved route and its drag distance
   * never start from a pile the server does not have.
   */
  const movePile = async (connectionId: number, at: LatLngDto) => {
    const previous = data.current.craneConnections.find(c => c.id === connectionId);
    if (!previous) return;
    const putBack = () => commit(d => ({...d, craneConnections: d.craneConnections.map(c => c.id === connectionId ? previous : c)}));
    commit(d => ({...d, craneConnections: d.craneConnections.map(c => c.id === connectionId ? {...c, pileLat: at.lat, pileLng: at.lng} : c)}));
    try {
      await updateCraneConnection(navigate, estimateId, connectionId, {pileLat: at.lat, pileLng: at.lng});
    } catch (e) {
      putBack();
      fail('Failed to move the staging pile')(e);
      return;
    }
    if (await followRouteStart(previous.treeId)) return;
    putBack();
    try {
      await updateCraneConnection(navigate, estimateId, connectionId, {pileLat: previous.pileLat ?? null, pileLng: previous.pileLng ?? null});
    } catch (e) {
      fail('Failed to move the staging pile')(e);
    }
  };

  // A route laid before its work item went on a crane still starts at the work item: lay it from the pile, as connecting now would.
  const healed = useRef<number | undefined>(undefined);
  useEffect(() => {
    if (!o.editable || !o.estimate || o.trees.length === 0 || healed.current === estimateId) return;
    healed.current = estimateId;
    data.current.craneConnections.forEach(link => {
      const first = data.current.dragPaths.find(p => p.treeId === link.treeId)?.points[0];
      const start = routeStartFor(link.treeId);
      if (first && start && distanceMeters(first, start) > 1) void resetRouteStraight(link.treeId);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [o.editable, !!o.estimate, o.trees.length, estimateId]);

  // ---- Gates ---------------------------------------------------------------------------------------------------

  const updateGateLocally = (gateId: number, change: (g: EstimateGateDetailsDto) => EstimateGateDetailsDto) =>
    commit(d => ({...d, gates: d.gates.map(g => g.id === gateId ? change(g) : g)}));

  /** Spaced by the icon's own ground width at the zoom the map opens at. */
  const addGate = async (onCreated?: (g: EstimateGateDetailsDto) => void) => {
    const e = opts.current.estimate;
    if (!e) return;
    const at = spawn(metersPerPx(e.lat, SITE_ZOOM) * GATE_ICON_WIDTH_DP * 0.8);
    setWorking(true);
    try {
      const created = await createGate(navigate, estimateId, {lat: at.lat, lng: at.lng});
      commit(d => ({...d, gates: [...d.gates, {...created, associatedTrees: created.associatedTrees ?? []}]}));
      onCreated?.(created);
    } catch (err) {
      fail('Failed to add gate')(err);
    } finally {
      setWorking(false);
    }
  };

  const moveGate = async (gateId: number, at: LatLngDto) => {
    const previous = data.current.gates.find(g => g.id === gateId);
    if (!previous) return;
    updateGateLocally(gateId, g => ({...g, lat: at.lat, lng: at.lng}));
    try {
      await updateGate(navigate, estimateId, gateId, {lat: at.lat, lng: at.lng});
    } catch (e) {
      updateGateLocally(gateId, g => ({...g, lat: previous.lat, lng: previous.lng}));
      fail('Failed to move gate')(e);
    }
  };

  /** SQ-286: turned to line up with the property. */
  const rotateGate = async (gateId: number, headingDeg: number) => {
    const previous = data.current.gates.find(g => g.id === gateId);
    if (!previous) return;
    const heading = norm360(headingDeg);
    updateGateLocally(gateId, g => ({...g, heading}));
    try {
      await updateGate(navigate, estimateId, gateId, {heading});
    } catch (e) {
      updateGateLocally(gateId, g => ({...g, heading: previous.heading}));
      fail('Failed to turn gate')(e);
    }
  };

  const deleteGate = async (gateId: number) => {
    setWorking(true);
    try {
      await deleteGateApi(navigate, estimateId, gateId);
      commit(d => ({...d, gates: d.gates.filter(g => g.id !== gateId)}));
    } catch (e) {
      fail('Failed to delete gate')(e);
    } finally {
      setWorking(false);
    }
  };

  /** Whole inches; 0 clears it. */
  const setGateWidth = async (gateId: number, inches: number) => {
    const previous = data.current.gates.find(g => g.id === gateId);
    if (!previous) return;
    const width = String(Math.min(GATE_MAX_INCHES, Math.max(0, inches)));
    if (previous.width === width) return;
    updateGateLocally(gateId, g => ({...g, width}));
    try {
      await updateGate(navigate, estimateId, gateId, {width});
    } catch (e) {
      updateGateLocally(gateId, g => ({...g, width: previous.width}));
      fail('Failed to save the gate width')(e);
    }
  };

  /** True while the work item's route touches the gate: it stays connected. */
  const pathThroughGate = (gateId: number, treeId: number) => crossings(data.current).has(`${gateId}:${treeId}`);

  const toggleGateWorkItem = (gateId: number, treeId: number) => {
    const gate = data.current.gates.find(g => g.id === gateId);
    if (!gate) return;
    const ids = gate.associatedTrees.map(t => t.id);
    if (ids.includes(treeId) && pathThroughGate(gateId, treeId)) return;
    updateGateLocally(gateId, g => withTrees(g, ids.includes(treeId) ? ids.filter(id => id !== treeId) : [...ids, treeId]));
    persistGateWorkItems(gateId);
  };

  /** Uploads the pictures, then adds them to the end of the gate's photos. */
  const uploadGatePhotos = async (gateId: number, files: File[]) => {
    if (files.length === 0) return;
    const previews = files.map(f => URL.createObjectURL(f));
    setGateUploads(u => ({...u, [gateId]: [...(u[gateId] ?? []), ...previews]}));
    try {
      const ids: number[] = [];
      for (const file of files) ids.push((await uploadImage(file)).id);
      const updated = await updateGate(navigate, estimateId, gateId, {imageIdsToAdd: ids});
      updateGateLocally(gateId, g => ({...g, images: updated.images, hiddenImages: updated.hiddenImages}));
    } catch (e) {
      fail('Gate photo upload failed — take the picture again')(e);
    } finally {
      setGateUploads(u => ({...u, [gateId]: (u[gateId] ?? []).filter(p => !previews.includes(p))}));
      previews.forEach(p => URL.revokeObjectURL(p));
    }
  };

  /** From the gate's own panel: the photo leaves the gate and every work item. */
  const deleteGatePhoto = async (gateId: number, imageId: number) => {
    const previous = data.current.gates.find(g => g.id === gateId);
    if (!previous) return;
    updateGateLocally(gateId, g => ({...g, images: g.images.filter(i => i.id !== imageId)}));
    try {
      await updateGate(navigate, estimateId, gateId, {imageIdsToRemove: [imageId]});
    } catch (e) {
      updateGateLocally(gateId, g => ({...g, images: previous.images}));
      fail('Failed to delete photo')(e);
    }
  };

  /**
   * From a work item's photos (SQ-716, Android's hideGatePhoto): the gate photo leaves THAT work item only; the gate and
   * every other connected work item keep it, until the work item is disconnected from the gate.
   */
  const hideGatePhoto = async (gateId: number, imageId: number, treeId: number) => {
    const previous = data.current.gates.find(g => g.id === gateId);
    if (!previous) return;
    updateGateLocally(gateId, g => ({...g, hiddenImages: [...(g.hiddenImages ?? []), {treeId, imageId}]}));
    try {
      await hideGateImage(navigate, estimateId, gateId, imageId, treeId);
    } catch (e) {
      updateGateLocally(gateId, g => ({...g, hiddenImages: previous.hiddenImages}));
      fail('Failed to remove photo')(e);
    }
  };

  /** Drawing on a gate photo saves it in place (SQ-286): same id, so every connected work item keeps it where it was. */
  const replaceGatePhoto = async (gateId: number, imageId: number, edit: PhotoEdit) => {
    try {
      const saved = await saveImageEdit(imageId, edit);
      updateGateLocally(gateId, g => ({...g, images: g.images.map(i => i.id === imageId ? {...i, imageUrl: saved.imageUrl} : i)}));
    } catch (e) {
      fail('Failed to save the edited photo')(e);
      throw e;
    }
  };

  const current = data.current;
  const lists = useMemo(() => ({
    gates: current.gates,
    chippers: current.additionalComponents.filter(c => c.type === CHIPPER_TYPE),
    cranes: current.additionalComponents.filter(c => c.type === CRANE_TYPE),
    dragPaths: current.dragPaths,
    craneConnections: current.craneConnections,
  }), [current.gates, current.additionalComponents, current.dragPaths, current.craneConnections]);

  const setViewZoom = useCallback((zoom: number) => { viewZoom.current = zoom; }, []);

  return {
    ...lists, working, gateUploads, view, setViewZoom,
    moveWorkItem, connectWorkItem, disconnectWorkItem, movePathPoint,
    addChipperTruck, moveChipperTruck, rotateChipperTruck, deleteChipperTruck,
    addCrane, moveCrane, rotateCrane, deleteCrane, connectCrane, disconnectCrane, movePile,
    addGate, moveGate, rotateGate, deleteGate, setGateWidth, toggleGateWorkItem, pathThroughGate,
    uploadGatePhotos, deleteGatePhoto, hideGatePhoto, replaceGatePhoto,
  };
}

export type SiteMap = ReturnType<typeof useSiteMap>;
